// src/ui/screens/verifications/VerificationManagementPage.jsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { verificationService } from '../../../services/verification/verificationService';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

// ── VerificationManagementPage ──────────────────────────────────────────────

export const VerificationManagementPage = () => {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showIdModal, setShowIdModal] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // ── Subscribe to pending verifications ──────────────────────────────────

  React.useEffect(() => {
    const q = query(
      collection(db, 'sellerVerifications'),
      where('status', '==', 'pending'),
      orderBy('submittedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVerifications(data);
      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching verifications:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // ── Format date ──────────────────────────────────────────────────────────

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // ── Approve verification ─────────────────────────────────────────────────

  const handleApprove = async (verificationId) => {
    setIsProcessing(true);
    try {
      const success = await verificationService.approveVerification(verificationId);
      if (success) {
        alert('Verification approved successfully');
      }
    } catch (error) {
      console.error('Error approving verification:', error);
      alert('Failed to approve verification');
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Reject verification ──────────────────────────────────────────────────

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setIsProcessing(true);
    try {
      const success = await verificationService.rejectVerification(
        selectedVerification,
        rejectionReason.trim()
      );
      if (success) {
        setShowRejectDialog(false);
        setRejectionReason('');
        alert('Verification rejected');
      }
    } catch (error) {
      console.error('Error rejecting verification:', error);
      alert('Failed to reject verification');
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Render info row ──────────────────────────────────────────────────────

  const renderInfoRow = (label, value) => {
    return (
      <div className="flex gap-4 py-1">
        <span className="text-white/50 text-xs w-24 flex-shrink-0">{label}</span>
        <span className="text-white text-xs flex-1 break-words">{value || 'Not provided'}</span>
      </div>
    );
  };

  // ── Render ID images modal ──────────────────────────────────────────────

  const renderIdModal = () => {
    if (!selectedVerification) return null;

    const data = verifications.find(v => v.id === selectedVerification);
    if (!data) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        onClick={() => setShowIdModal(false)}
      >
        <div
          className="w-full max-w-sm mx-4 p-5 rounded-xl"
          style={{ backgroundColor: COLORS.card }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-white text-lg font-semibold text-center">ID Images</h3>
          <div className="space-y-3 mt-4">
            {data.frontIdUrl && (
              <img
                src={data.frontIdUrl}
                alt="Front ID"
                className="w-full h-[200px] rounded-xl object-cover"
              />
            )}
            {data.backIdUrl && (
              <img
                src={data.backIdUrl}
                alt="Back ID"
                className="w-full h-[200px] rounded-xl object-cover"
              />
            )}
          </div>
          <button
            onClick={() => setShowIdModal(false)}
            className="w-full mt-4 py-3 rounded-xl bg-filatom-primary text-black font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  // ── Render reject dialog ─────────────────────────────────────────────────

  const renderRejectDialog = () => {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        onClick={() => setShowRejectDialog(false)}
      >
        <div
          className="w-full max-w-sm mx-4 p-5 rounded-xl"
          style={{ backgroundColor: COLORS.card }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-white text-lg font-semibold">Reject Verification</h3>
          <p className="text-white/70 text-xs mt-2">Please provide a reason for rejection:</p>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3}
            className="w-full mt-3 p-3 rounded-xl text-white text-xs outline-none resize-none placeholder:text-white/30"
            style={{ backgroundColor: COLORS.cardLight }}
            placeholder="Enter reason..."
          />
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => setShowRejectDialog(false)}
              className="flex-1 py-3 rounded-xl text-white/50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={isProcessing}
              className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold disabled:opacity-50"
            >
              {isProcessing ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
              ) : (
                'Reject'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* App Bar */}
      <div className="pt-safe-top px-4 py-3 bg-filatom-bg">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-white p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-white text-lg font-semibold">Verification Requests</span>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : verifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <svg className="w-16 h-16 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <p className="text-white/40 text-sm mt-4">No pending verifications</p>
          </div>
        ) : (
          verifications.map((verification) => (
            <div
              key={verification.id}
              className="mt-4 p-4 rounded-xl"
              style={{ backgroundColor: COLORS.card }}
            >
              {/* Header */}
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${COLORS.orange}1F` }}
                >
                  <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-semibold">
                    {verification.sellerCategory || 'Unknown'}
                  </p>
                  <p className="text-white/40 text-xs">
                    {verification.userEmail || 'No email'}
                  </p>
                </div>
                <div
                  className="px-2 py-0.5 rounded text-[11px] font-semibold"
                  style={{
                    backgroundColor: 'rgba(255,140,8,0.12)',
                    color: COLORS.orange,
                  }}
                >
                  Pending
                </div>
              </div>

              <div className="h-px bg-white/5 my-3" />

              {/* Details */}
              {renderInfoRow('Physical Address', verification.physicalAddress)}
              {renderInfoRow('Submitted', formatDate(verification.submittedAt))}

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setSelectedVerification(verification.id);
                    setShowIdModal(true);
                  }}
                  className="flex-1 py-2.5 rounded-xl text-filatom-primary text-xs font-semibold border border-filatom-primary"
                >
                  View IDs
                </button>
                <button
                  onClick={() => handleApprove(verification.id)}
                  disabled={isProcessing}
                  className="flex-1 py-2.5 rounded-xl bg-green-500 text-white text-xs font-semibold disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  onClick={() => {
                    setSelectedVerification(verification.id);
                    setShowRejectDialog(true);
                  }}
                  disabled={isProcessing}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-xs font-semibold disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modals */}
      {showIdModal && renderIdModal()}
      {showRejectDialog && renderRejectDialog()}
    </div>
  );
};

export default VerificationManagementPage;